import { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { checkAHAVI, replaceHtmlToStr } from 'utils/utils';
import {
  getHotelInfoDescription,
  getHotelInfoFacilityText,
  getHotelInfoImageUrl,
  HotelInfoConfig,
  resolveHotelInfo,
} from 'utils/shared/hotelProposalInformation';
import { EditProposalContext } from 'contexts/EditProposaContext';

interface ProposalHtmlHotelInformationProps {
  hotelInformation?: any;
  config?: HotelInfoConfig;
}

const ProposalHtmlHotelInformation: FC<ProposalHtmlHotelInformationProps> = ({
  hotelInformation,
  config,
}) => {
  const { t } = useTranslation();
  const { editMode } = useContext(EditProposalContext);
  const hotelInfo = resolveHotelInfo(hotelInformation);

  if (!config || !hotelInfo) {
    return null;
  }

  const hasAnyChecked =
    config.details || config.overview || !!config.imagesIndexes?.length;

  if (!hasAnyChecked) {
    return null;
  }

  const description = getHotelInfoDescription(hotelInfo, config.language);
  const displayImages =
    (hotelInfo.images || []).filter((_: any, index: number) => config.imagesIndexes?.includes(index));
  const hasDetails =
    config.details &&
    (!!hotelInfo.checkIn || !!hotelInfo.checkOut || !!hotelInfo.hotelFacilities?.length);
  const hasOverview = config.overview && !!description;
  const hasImages = editMode
    ? !!config.imagesIndexes?.length && !!displayImages.length
    : !!displayImages.length;

  if (!hasDetails && !hasImages && !hasOverview) {
    return null;
  }

  return (
    <div className="pr-hotel-div">
      <div className="pr-hotel-title">{t('hotel.info')}</div>
      {hasDetails && (
        <div className="pr-break-avoid pr-hotel-info-block">
          <div className="pr-hotel-title">{t('hotel.details')}</div>
          {!!hotelInfo.checkIn && (
            <div className="pr-hotel-info-section">
              <div className="pr-hotel-info-label">{t('search.check_in')}</div>
              <div
                className="pr-hotel-info-text"
                dangerouslySetInnerHTML={{ __html: replaceHtmlToStr(hotelInfo.checkIn) }}
              />
            </div>
          )}
          {!!hotelInfo.checkOut && (
            <div className="pr-hotel-info-section">
              <div className="pr-hotel-info-label">{t('search.check_out')}</div>
              <div
                className="pr-hotel-info-text"
                dangerouslySetInnerHTML={{ __html: replaceHtmlToStr(hotelInfo.checkOut) }}
              />
            </div>
          )}
          {!!hotelInfo.hotelFacilities?.length && (
            <div className="pr-hotel-info-section">
              <div className="pr-hotel-info-label">{t('hotel.facilities')}</div>
              <div className="pr-hotel-info-facilities row">
                {hotelInfo.hotelFacilities.map((facility: any, index: number) => (
                  <div key={index} className="col-4">{getHotelInfoFacilityText(facility)}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {hasImages && (
        <div className="pr-break-avoid pr-hotel-info-block">
          <div className="pr-hotel-title">{t('hotel.images')}</div>
          <div className="pr-hotel-info-images">
            {displayImages.map((image: any, index: number) => (
              <div className="pr-hotel-info-image" key={index}>
                <img
                  src={getHotelInfoImageUrl(image)}
                  alt=""
                  onError={(e) => {
                    (e.target as any).parentNode.style.display = 'none';
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {hasOverview && (
        <div className="pr-break-avoid pr-hotel-info-block">
          <div className="pr-hotel-title">{t('hotel.overview')}</div>
          <div
            className={
              'pr-hotel-info-text' + (checkAHAVI(description) ? ' pr-rtl' : ' pr-ltr')
            }
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      )}
    </div>
  );
};

export default ProposalHtmlHotelInformation;
